import { BrowserRouter, Routes, Route, NavLink, Navigate, useLocation } from 'react-router-dom';
import { useEffect, useState, Component, lazy, Suspense } from 'react';

// Deliberate-crash route for verifying ErrorBoundary + Sentry wiring end-to-end.
// Only throws when URL has ?confirm=yes — so a stray user / bot can't trigger it.
// Without the param, shows a confirmation landing page with a button.
function CrashTestRoute() {
  const qs = new URLSearchParams(window.location.search);
  if (qs.get('confirm') === 'yes') {
    // This error is caught by <ErrorBoundary> wrapping <App/> in main.jsx,
    // which renders the branded AppCrashFallback and ships the error to Sentry.
    throw new Error('Deliberate crash — ErrorBoundary + Sentry verification (safe to ignore)');
  }
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Inter', -apple-system, sans-serif", background: '#FDFBF7', padding: 24,
    }}>
      <div style={{ maxWidth: 440, textAlign: 'center' }}>
        <div style={{ fontSize: 14, fontFamily: "'JetBrains Mono', monospace", color: '#C08B2A', letterSpacing: '0.08em', marginBottom: 16 }}>
          JUUNI · INTERNAL
        </div>
        <h1 style={{ fontSize: 20, fontWeight: 600, margin: '0 0 12px' }}>Crash test</h1>
        <p style={{ fontSize: 13, color: '#666', margin: '0 0 24px', lineHeight: 1.6 }}>
          Clicking below intentionally crashes the React tree so we can verify the
          error boundary fallback renders and Sentry captures the event.
        </p>
        <button
          onClick={() => { window.location.search = '?confirm=yes'; }}
          style={{
            padding: '12px 24px', border: 'none', background: '#c44',
            color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 500,
          }}>
          Trigger crash
        </button>
      </div>
    </div>
  );
}

// Legacy AdminErrorBoundary — preserved for backward compat; new pages use <ErrorBoundary>
class AdminErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error('Admin crash:', error, info.componentStack); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 40, fontFamily: 'monospace', maxWidth: 800, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.6 }}>⚠️</div>
          <h2 style={{ color: '#ef4444', fontSize: 16 }}>Something went wrong</h2>
          <p style={{ color: '#888', fontSize: 12, marginBottom: 20 }}>An unexpected error occurred. Please refresh the page.</p>
          {import.meta.env.DEV && this.state.error && (
            <pre style={{ whiteSpace: 'pre-wrap', fontSize: 10, textAlign: 'left', background: '#1a1a1a', color: '#ff6b6b', padding: 16, borderRadius: 8, marginBottom: 16, maxHeight: 200, overflow: 'auto' }}>
              {this.state.error.message}{'\n'}{this.state.error.stack}
            </pre>
          )}
          <button onClick={() => { this.setState({ hasError: false }); window.location.reload(); }}
            style={{ padding: '8px 20px', background: '#5DCAA5', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
import { AuthProvider, useAuth } from './context/AuthContext';
import { AppProvider } from './context/AppContext';
import { DataProvider } from './context/DataProvider';
import ProtectedRoute from './components/ProtectedRoute';
import RoleRoute from './components/RoleRoute';
import ImpersonationBanner from './components/ImpersonationBanner';
import ErrorBoundary from './components/ErrorBoundary';
import ReportIssueButton from './components/ReportIssueButton';
import { ToastProvider } from './components/Toast';
import { ConfirmProvider } from './components/ConfirmDialog';
import { GodModeProvider } from './context/GodModeContext';
import GodModePanel from './components/GodModePanel';
// ForYou removed — social feature, not part of ops platform
// Social layer removed for initial launch (saved in snapshots/social-layer/)
import JobDetail from './pages/JobDetail';
import Jobs from './pages/Jobs';
// Legal pages — public, no auth required
const LegalMSA = lazy(() => import('./pages/legal/MSA'));
const LegalTerms = lazy(() => import('./pages/legal/Terms'));
const LegalPrivacy = lazy(() => import('./pages/legal/Privacy'));
const LegalDPA = lazy(() => import('./pages/legal/DPA'));
const SLA = lazy(() => import('./pages/legal/SLA'));
// Customer-facing docs — public, no auth required
const DocsIndex = lazy(() => import('./pages/docs/DocsIndex'));
const DocsFirstProject = lazy(() => import('./pages/docs/FirstProject'));
const DocsInviteTeam = lazy(() => import('./pages/docs/InviteTeam'));
const DocsBatchQA = lazy(() => import('./pages/docs/BatchQA'));
const DocsClientReport = lazy(() => import('./pages/docs/ClientReport'));
const DocsBillingPlans = lazy(() => import('./pages/docs/BillingPlans'));
import Settings from './pages/Settings';
import Professional from './pages/Professional';
import AnnotatorDashboard from './pages/AnnotatorDashboard';
import AnnotatorLayout from './layouts/AnnotatorLayout';
import Login from './pages/Login';
import Signup from './pages/Signup';
// Phase 5.75-vocab Commit A (2026-04-26): Client Portal invite-acceptance landing
import ClientAcceptInvite from './pages/ClientAcceptInvite';
// Onboarding lazy-loaded below
// Lazy-loaded pages (code-split — only loaded when navigated to)
const Admin = lazy(() => import('./pages/Admin'));
import AdminLayout from './layouts/AdminLayout'; // Eagerly loaded — must never unmount during admin navigation
const SPLDashboard = lazy(() => import('./pages/admin/SPLDashboard'));
const AdminProjects = lazy(() => import('./pages/admin/AdminProjects'));
// Phase 5.75 Stage A — Dataset Intelligence catalog + detail routes
const Datasets = lazy(() => import('./pages/admin/Datasets'));
const DatasetDetail = lazy(() => import('./pages/admin/DatasetDetail'));
// Phase 6 — CSV ingest UI (admin + SPL)
const CSVIngestPage = lazy(() => import('./pages/admin/CSVIngestPage'));
// Phase 8 — LLM digest approval UI (admin only; dry-run workflow)
const DigestApprovalPage = lazy(() => import('./pages/admin/DigestApprovalPage'));
const AdminWorkforce = lazy(() => import('./pages/admin/AdminWorkforce'));
const AdminReports = lazy(() => import('./pages/admin/AdminReports'));
const AdminAnalytics = lazy(() => import('./pages/admin/AdminAnalytics'));
const AdminAudit = lazy(() => import('./pages/admin/AdminAudit'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminPlaceholder = lazy(() => import('./pages/admin/AdminPlaceholder'));
const BossDashboard = lazy(() => import('./pages/admin/BossDashboard'));
const SPLTeams = lazy(() => import('./pages/admin/SPLTeams'));
const AdminFinancials = lazy(() => import('./pages/admin/AdminFinancials'));
const OrgBillingSettings = lazy(() => import('./pages/admin/OrgBillingSettings'));
const JuuniBilling = lazy(() => import('./pages/god/JuuniBilling'));
const MyProfile = lazy(() => import('./pages/admin/MyProfile'));
const AdminClients = lazy(() => import('./pages/admin/AdminClients'));
const AnnotatorProfile = lazy(() => import('./pages/admin/AnnotatorProfile'));
const SPLProfile = lazy(() => import('./pages/admin/SPLProfile'));
const ClientProfile = lazy(() => import('./pages/admin/ClientProfile'));
const CapacityPlanning = lazy(() => import('./pages/admin/CapacityPlanning'));
const CreateProject = lazy(() => import('./pages/admin/CreateProject'));
const PostMarketplaceJob = lazy(() => import('./pages/admin/PostMarketplaceJob'));
const ProjectDashboard = lazy(() => import('./pages/ProjectDashboard'));
const Integrations = lazy(() => import('./pages/Integrations'));
const ClientPortal = lazy(() => import('./pages/ClientPortal'));
const AnalyticsDashboard = lazy(() => import('./pages/AnalyticsDashboard'));
const OrgSetupWizard = lazy(() => import('./pages/OrgSetupWizard'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const Profile = lazy(() => import('./pages/Profile'));
const RequestAccess = lazy(() => import('./pages/RequestAccess'));
import './App.css';

/** Redirect non-annotator roles to their proper dashboard when they hit / */
function RoleRedirect({ children }) {
  const { role } = useAuth();
  if (role === 'admin' || role === 'spl') return <Navigate to="/admin" replace />;
  if (role === 'god') return <Navigate to="/god" replace />;
  if (role === 'client') return <Navigate to="/client" replace />;
  return children;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    const main = document.querySelector('.main-content');
    if (main) main.scrollTop = 0;
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function LegacyOnboardEntry() {
  const location = useLocation();
  const { user } = useAuth();
  return <Navigate to={`${user ? '/onboarding' : '/signup'}${location.search}${location.hash}`} replace />;
}

function LegacyJoinEntry() {
  const location = useLocation();
  return <Navigate to={`/signup${location.search}`} replace />;
}

function NotFoundPage() {
  const location = useLocation();
  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '48px 24px', color: 'var(--text-secondary)' }}>
      <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>
        Route Not Found
      </div>
      <div style={{ fontSize: 24, fontWeight: 600, color: 'var(--text-white)', marginBottom: 10 }}>
        This page does not exist.
      </div>
      <div style={{ fontSize: 13, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', marginBottom: 20 }}>
        {location.pathname}
        {location.search}
      </div>
      <NavLink to="/jobs" className="back-link" style={{ textDecoration: 'none' }}>
        ← Back to Jobs
      </NavLink>
    </div>
  );
}

function AdminDashboardRouter() {
  const { isAdmin } = useAuth();
  return isAdmin ? <BossDashboard /> : <SPLDashboard />;
}

// AnnotatorLayout moved to src/layouts/AnnotatorLayout.jsx

// Global unhandled rejection handler — prevents silent failures
// Dispatches a custom event that the ToastProvider can listen for
if (typeof window !== 'undefined' && !window.__juuniGlobalHandlerAdded) {
  window.__juuniGlobalHandlerAdded = true;
  window.addEventListener('unhandledrejection', (event) => {
    console.error('[Unhandled Promise Rejection]', event.reason);
    window.dispatchEvent(new CustomEvent('juuni:error', {
      detail: { message: 'A background operation failed. Your data may not be saved.' },
    }));
    // Send to Sentry
    import('./lib/sentry').then(({ captureError }) => {
      captureError(event.reason instanceof Error ? event.reason : new Error(String(event.reason)), { source: 'unhandledrejection' });
    }).catch(() => {});
  });
}

// Offline detection banner
function OfflineBanner() {
  const [offline, setOffline] = useState(!navigator.onLine);
  useEffect(() => {
    const goOffline = () => setOffline(true);
    const goOnline = () => setOffline(false);
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => { window.removeEventListener('offline', goOffline); window.removeEventListener('online', goOnline); };
  }, []);
  if (!offline) return null;
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 99999,
      background: '#ef4444', color: '#fff', textAlign: 'center',
      padding: '6px 16px', fontSize: 11, fontFamily: 'var(--font-mono, monospace)', fontWeight: 600,
    }}>
      You're offline. Changes won't be saved until your connection returns.
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
      <ConfirmProvider>
      <AuthProvider>
      <GodModeProvider>
      <DataProvider>
      <AppProvider>
        <OfflineBanner />
        <ImpersonationBanner />
        <GodModePanel />
        <ReportIssueButton />
        <Suspense fallback={<div style={{ padding: 40, textAlign: 'center', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', fontSize: 11 }}>Loading...</div>}>
        <Routes>
          {/* Internal verification route — intentionally crashes to prove ErrorBoundary + Sentry wiring.
              Guarded by ?confirm=yes so nobody hits it accidentally. Safe to leave in prod. */}
          <Route path="/__verify/crash" element={<CrashTestRoute />} />

          {/* Public legal pages — no auth needed. Customer procurement teams,
              Stripe compliance reviewers, and prospects must be able to read
              these without an account. */}
          <Route path="/legal/msa" element={<ErrorBoundary label="MSA"><LegalMSA /></ErrorBoundary>} />
          <Route path="/legal/terms" element={<ErrorBoundary label="Terms"><LegalTerms /></ErrorBoundary>} />
          <Route path="/legal/privacy" element={<ErrorBoundary label="Privacy"><LegalPrivacy /></ErrorBoundary>} />
          <Route path="/legal/dpa" element={<ErrorBoundary label="DPA"><LegalDPA /></ErrorBoundary>} />
          <Route path="/sla" element={<ErrorBoundary label="SLA"><SLA /></ErrorBoundary>} />

          {/* Public customer docs — no auth. Prospects qualifying Juuni + existing
              users self-serving answers land here. 5 quickstart pages today;
              extract to Mintlify at docs.juuniai.com when we hit ~20 pages. */}
          <Route path="/docs" element={<ErrorBoundary label="Docs"><DocsIndex /></ErrorBoundary>} />
          <Route path="/docs/first-project" element={<ErrorBoundary label="Docs:FirstProject"><DocsFirstProject /></ErrorBoundary>} />
          <Route path="/docs/invite-team" element={<ErrorBoundary label="Docs:InviteTeam"><DocsInviteTeam /></ErrorBoundary>} />
          <Route path="/docs/batch-qa" element={<ErrorBoundary label="Docs:BatchQA"><DocsBatchQA /></ErrorBoundary>} />
          <Route path="/docs/client-report" element={<ErrorBoundary label="Docs:ClientReport"><DocsClientReport /></ErrorBoundary>} />
          <Route path="/docs/billing-plans" element={<ErrorBoundary label="Docs:BillingPlans"><DocsBillingPlans /></ErrorBoundary>} />

          {/* Public marketplace lead capture — wired from the landing page's
              "Request access" CTAs. No auth required. */}
          <Route path="/request-access" element={<ErrorBoundary label="Request Access"><RequestAccess /></ErrorBoundary>} />

          {/* Auth & Onboarding */}
          <Route path="/login" element={<ErrorBoundary label="Login"><Login /></ErrorBoundary>} />
          <Route path="/signup" element={<ErrorBoundary label="Signup"><Signup /></ErrorBoundary>} />
          {/* Phase 5.75-vocab Commit A: Client Portal magic-link redemption.
              PUBLIC route — invitees may not be signed in yet. The page itself
              orchestrates auth flow (signup or sign-in) before redeeming. */}
          <Route path="/client/accept/:token" element={<ErrorBoundary label="Accept Invite"><ClientAcceptInvite /></ErrorBoundary>} />
          <Route path="/onboarding" element={<ProtectedRoute><ErrorBoundary label="Onboarding"><Onboarding /></ErrorBoundary></ProtectedRoute>} />
          <Route path="/setup" element={<RoleRoute allowedRoles={['admin', 'spl', 'god']}><ErrorBoundary label="Org Setup"><OrgSetupWizard /></ErrorBoundary></RoleRoute>} />
          <Route path="/onboard" element={<LegacyOnboardEntry />} />
          <Route path="/join" element={<LegacyJoinEntry />} />

          {/* Admin/Ops — with sidebar layout, for SPL + Admin + God */}
          <Route path="/admin" element={<RoleRoute allowedRoles={['admin', 'spl', 'god']}><AdminLayout /></RoleRoute>}>
            <Route index element={<AdminDashboardRouter />} />
            {/* Cross-phase sweep 2026-04-24 fix 9.1 — direct /admin/dashboard URL was 404 (sidebar uses /admin index). Redirect for muscle-memory typing. */}
            <Route path="dashboard" element={<Navigate to="/admin" replace />} />
            <Route path="projects" element={<AdminProjects />} />
            {/* Phase 5.75 Stage A — Datasets catalog (Commit 5) + detail page (Commit 6) */}
            <Route path="datasets" element={<Datasets />} />
            <Route path="dataset/:id" element={<DatasetDetail />} />
            {/* Phase 6 — CSV ingest. Admin + SPL; role check enforced inside the component. */}
            <Route path="ingest" element={<CSVIngestPage />} />
            {/* Phase 8 — Digest approvals. Admin only; dry-run workflow for LLM-narrated weekly SPL digests. */}
            <Route path="digests" element={<DigestApprovalPage />} />
            <Route path="workforce" element={<AdminWorkforce />} />
            {/* Phase 5.75-vocab follow-up (2026-04-26): Applications was
                merged into the Annotators page as a tab. Old links to
                /admin/applications redirect to /admin/workforce?tab=applications
                so external bookmarks + the BossDashboard's existing
                callouts keep working. */}
            <Route path="applications" element={<Navigate to="/admin/workforce?tab=applications" replace />} />
            <Route path="reports" element={<AdminReports />} />
            <Route path="analytics" element={<AdminAnalytics />} />
            <Route path="audit" element={<AdminAudit />} />
            <Route path="profile" element={<MyProfile />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="project/:jobId" element={<ErrorBoundary label="Project Dashboard"><ProjectDashboard /></ErrorBoundary>} />
            <Route path="annotator/:annotatorId" element={<ErrorBoundary label="Annotator Profile"><AnnotatorProfile /></ErrorBoundary>} />
            <Route path="spl/:splId" element={<ErrorBoundary label="SPL Profile"><SPLProfile /></ErrorBoundary>} />
            <Route path="client/:clientId" element={<ErrorBoundary label="Client Profile"><ClientProfile /></ErrorBoundary>} />
            {/* Admin-only pages */}
            <Route path="spl-teams" element={<RoleRoute allowedRoles={['admin', 'god']}><SPLTeams /></RoleRoute>} />
            <Route path="financials" element={<RoleRoute allowedRoles={['admin', 'god']}><AdminFinancials /></RoleRoute>} />
            <Route path="billing-settings" element={<RoleRoute allowedRoles={['admin', 'spl', 'god']}><OrgBillingSettings /></RoleRoute>} />
            <Route path="clients" element={<AdminClients />} />
            <Route path="capacity" element={<RoleRoute allowedRoles={['admin', 'god']}><CapacityPlanning /></RoleRoute>} />
            <Route path="create-project" element={<CreateProject />} />
            <Route path="marketplace-jobs" element={<PostMarketplaceJob />} />
            {/* Catch-all for any /admin/* path that didn't match — shows the
                same NotFoundPage as the top-level catch-all so a fat-fingered
                link doesn't silently drop the user on the dashboard. */}
            <Route path="*" element={<NotFoundPage />} />
          </Route>

          {/* God/Founder — full platform view, god role only */}
          <Route path="/god" element={<RoleRoute allowedRoles={['god']}><AdminErrorBoundary><Admin /></AdminErrorBoundary></RoleRoute>} />
          <Route path="/god/billing" element={<RoleRoute allowedRoles={['god']}><ErrorBoundary label="Juuni Billing"><JuuniBilling /></ErrorBoundary></RoleRoute>} />

          {/* Client Portal — standalone, no sidebar, protected */}
          <Route path="/client/*" element={<RoleRoute allowedRoles={['client', 'admin', 'god']}><ErrorBoundary label="Client Portal"><ClientPortal /></ErrorBoundary></RoleRoute>} />

          {/* Annotator — with sidebar, auth-protected, redirects admin/SPL to their dashboard */}
          <Route path="/" element={<ProtectedRoute><RoleRedirect><AnnotatorLayout /></RoleRedirect></ProtectedRoute>}>
            <Route index element={<ErrorBoundary label="Dashboard"><AnnotatorDashboard /></ErrorBoundary>} />
            <Route path="find-work" element={<ErrorBoundary label="Find Work"><Jobs /></ErrorBoundary>} />
            <Route path="my-work" element={<ErrorBoundary label="My Work"><Jobs /></ErrorBoundary>} />
            <Route path="assessments" element={<ErrorBoundary label="Assessments"><Jobs /></ErrorBoundary>} />
            <Route path="earnings" element={<ErrorBoundary label="Earnings"><Jobs /></ErrorBoundary>} />
            <Route path="profile" element={<ErrorBoundary label="Profile"><Professional /></ErrorBoundary>} />
            <Route path="profile/:id" element={<ErrorBoundary label="Profile"><Suspense fallback={<div style={{ padding: 40 }}>Loading...</div>}><Profile /></Suspense></ErrorBoundary>} />
            <Route path="job/:id" element={<ErrorBoundary label="Job Detail"><JobDetail /></ErrorBoundary>} />
            <Route path="professional" element={<ErrorBoundary label="Professional"><Professional /></ErrorBoundary>} />
            <Route path="settings" element={<ErrorBoundary label="Settings"><Settings /></ErrorBoundary>} />
            <Route path="jobs" element={<Navigate to="/" replace />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
        </Suspense>
      </AppProvider>
      </DataProvider>
      </GodModeProvider>
      </AuthProvider>
      </ConfirmProvider>
      </ToastProvider>
    </BrowserRouter>
  );
}

export default App;
